import Axios from 'axios';
import { getLocale } from '@rcm/i18n-client';
import type { CreditCardData } from 'cart-ui-react';
import { getWindowPropAsync } from './lib/getWindowPropAsync';
import {
  setCurrentTransactionData,
  getCurrentTransactionData,
  deleteCurrentTransactionData,
} from './currentTransactionBrowserSessionRepo';
import type { Transaction } from './types/Transaction';

interface IThreeDS {
  configure: (configuration: any) => any;
  isConfigured: () => boolean;
  initiateAuthentication: (orderId: string, transactionId: string, callback: (data: any) => void) => undefined;
  authenticatePayer: (
    orderId: string,
    transactionId: string,
    callback: (data: any) => void,
    optionalParams?: object
  ) => undefined;
}

type IOnAuthCompletion = (transaction: Transaction) => void;
type IOnGatewaySaysDoNotProceed = (Object) => void;
type IOnAuthenticationNetworkingError = (error: Error) => void;

export default class Chase3dsManager {
  protected onAuthCompletion: IOnAuthCompletion;
  protected onGatewaySaysDoNotProceed: IOnGatewaySaysDoNotProceed;
  protected onAuthenticationNetworkingError: IOnAuthenticationNetworkingError;
  protected threeDS: IThreeDS;
  protected transaction: Transaction;
  protected logger: { info: (message?: any, ...optionalParams: any[]) => void };
  protected authenticationIframe: { element: HTMLIFrameElement; interval: NodeJS.Timeout };

  constructor(
    onAuthCompletion: IOnAuthCompletion,
    onGatewaySaysDoNotProceed: IOnGatewaySaysDoNotProceed,
    onAuthenticationNetworkingError: IOnAuthenticationNetworkingError
  ) {
    this.beginTransaction = this.beginTransaction.bind(this);
    this.initiateAuthentication = this.initiateAuthentication.bind(this);
    this.handleInitiateAuthenticationResult = this.handleInitiateAuthenticationResult.bind(this);
    this.authenticatePayer = this.authenticatePayer.bind(this);
    this.handleAuthenticatePayerResult = this.handleAuthenticatePayerResult.bind(this);

    this.onAuthCompletion = onAuthCompletion;
    this.onGatewaySaysDoNotProceed = onGatewaySaysDoNotProceed;
    this.onAuthenticationNetworkingError = onAuthenticationNetworkingError;
    this.authenticationIframe = { element: null, interval: null };
  }
  public handlePossiblePreviousAuthAttempt() {
    if (!window.location.href.includes('chase3dsHandlingAuthRedirect=1')) {
      return;
    }
    if (window.location.href.includes('chase3dsProceedWithPayment=1')) {
      const browserSessionTransactionData = getCurrentTransactionData();
      if (browserSessionTransactionData === null) {
        this.onGatewaySaysDoNotProceed({
          situationDescription:
            'handlePossiblePreviousAuthAttempt() found we are missing transaction data we so treated it as a declined card',
        });
      } else {
        this.transaction = browserSessionTransactionData;
        deleteCurrentTransactionData(); // Delete the temporary browser session transaction data
        this.onAuthCompletion(this.transaction);
      }
    } else {
      this.onGatewaySaysDoNotProceed({
        situationDescription:
          'handlePossiblePreviousAuthAttempt() found that our own server said to treat it as a declined' +
          ' card because it did not put chase3dsProceedWithPayment=1 in the redirect URL',
      });
    }
  }

  public async beginTransaction(containerElementId: string, cardData: CreditCardData, chase3dsVersion: string) {
    try {
      await this.createTransaction(cardData);

      await this.configureThreeDSUI(containerElementId, chase3dsVersion);

      setCurrentTransactionData(this.transaction);
      this.initiateAuthentication();
    } catch (error) {
      this.onAuthenticationNetworkingError(error);
    }
  }

  protected async createTransaction(cardData: CreditCardData) {
    const url = '/api/order/current/chase3dsTransaction';
    const response = await Axios.post(url, cardData);
    if (
      !response.data.merchantId ||
      !response.data.orderId ||
      !response.data.sessionId ||
      !response.data.transactionId
    ) {
      throw new Error(
        'An error occurred while calling "' + url + '". Some response properties were missing or invalid'
      );
    }
    this.transaction = {
      cardData,
      merchantId: response.data.merchantId,
      orderId: response.data.orderId,
      sessionId: response.data.sessionId,
      transactionId: response.data.transactionId,
    };
  }

  protected ensureThreeDSJSLoaded(): Promise<void> {
    return new Promise((resolve, reject) => {
      getWindowPropAsync<IThreeDS>('ThreeDS').then((ThreeDS) => {
        this.threeDS = ThreeDS;
        resolve();
      });
    });
  }

  protected configureThreeDSUI(containerElementId: string, chase3dsVersion: string): Promise<void> {
    const wsVersion = Number(chase3dsVersion);
    // this.logger.info('configureThreeDSUI start');
    return new Promise((resolve, reject) => {
      this.ensureThreeDSJSLoaded().then(() => {
        // this.logger.info(
        //     'Calling ThreeDS.configure with ',
        //     { containerElementId, merchantId: this.merchantId, sessionId: this.sessionId }
        // );

        this.threeDS.configure({
          callback: () => {
            if (this.threeDS.isConfigured()) {
              // this.logger.info('configureThreeDSUI done');
              resolve();
            } else {
              reject(new Error('3DS configure completed but isConfigured() is returing false'));
            }
          },
          frameEmbeddingMitigation: ["javascript", "x-frame-options", "csp"],
          configuration: {
            userLanguage: this.getUserLanguage(),
            wsVersion: wsVersion,
          },
          containerId: containerElementId,
          merchantId: this.transaction.merchantId,
          sessionId: this.transaction.sessionId,
          optionalParams: {
            fullScreenRedirect: true,
          },
        });
      });
    });
  }

  protected initiateAuthentication() {
    try {
      this.threeDS.initiateAuthentication(
        this.transaction.orderId,
        this.transaction.transactionId,
        this.handleInitiateAuthenticationResult
      );
    } catch (e) {
      this.onAuthenticationNetworkingError(new Error('ThreeDS.initiateAuthentication threw an error'));
      return;
    }
  }

  protected handleInitiateAuthenticationResult(data) {
    if (!data) {
    this.onAuthenticationNetworkingError(new Error('ThreeDS.initiateAuthentication returned no data.'));
      return;
    } else if (data.error) {
      this.onAuthenticationNetworkingError(new Error('ThreeDS.initiateAuthentication returned an error'));
      return;
    } else {
      /**
       * This is kludgy, but we wait 10 seconds between initiateAuthentication() and authenticatePayer() because of the following email
       * we got from Chase Support after asking them about their system randomly 503'ing from an HTTP API that authenticatePayer() calls.
       *
       * The Email:
       * "Hello All,
       * We received the following from our developers:
       * After investigation, it appears that the merchant only allowed 3 seconds between the Initiate Authentication request and the
       * Authenticate payer request which resulted in the Authenticate Payer request being sent before the ACS Method call was completed.
       * In these instances, the gateway will return the 503 error that you've provided in the problem description.
       *
       * Once the ACS method call has been completed, or ten seconds have elapsed, the request will be allowed to proceed as seen with the
       * subsequent Authentication request. We suggest that the merchant wait 10 seconds before sending the Authenticate Payer request if
       * they wish to be completely sure to alleviate the issue.
       * Please let us know if you have any question or if there is no further update, this case will be automatically close within the
       * next 24 hours."
       *
       */

      setTimeout(this.authenticatePayer, 10000);
    }
  }

  protected authenticatePayer() {
    try {
      this.threeDS.authenticatePayer(
        this.transaction.orderId,
        this.transaction.transactionId,
        this.handleAuthenticatePayerResult,
        { fullScreenRedirect: true }
      );
    } catch (e) {
      this.onAuthenticationNetworkingError(new Error('ThreeDS.authenticatePayer threw an error'));
      return;
    }
  }

  protected handleAuthenticatePayerResult(data) {
    if (!data) {
      this.onAuthenticationNetworkingError(new Error('ThreeDS.authenticatePayer returned no data.'));
      return;
    } else if (data.error) {
      this.onAuthenticationNetworkingError(
        new Error('ThreeDS.authenticatePayer returned error: ' + JSON.stringify(data.error))
      );
      return;
    }
    // We don't need to do anything further here, Chase's code will redirect the user.
  }

  protected getUserLanguage() {
    return getLocale().replace('_', '-');
  }
}
